// Guided set-up (proposed steps 0.35 "Read your website", 0.37 "Choose your route"; docs/specs/GUIDED_SETUP.md): the
// call to the setup Edge Function and the shapes it returns. The draft is saved on the server as suggestions (never
// confirmed); the screens confirm through act (lib/crm.ts saveCore, setRoute, setModuleState, setModulePart).
//
// Demo mode: the demo transport (src/demo) answers POST /functions/v1/setup; it can build the same answer with
// demoSetupAnswer() below, which uses the keyword rules (setupRules.ts) instead of Claude.
import { env, functionUrl } from "./env";
import { httpFetch } from "./http";
import {
  defaultModules, defaultParts, draftFromText, routeFromGoal, type BusinessType, type ModuleKey, type Route, type SetupDraft,
} from "./setupRules";

export { draftFromText, routeFromGoal };
export type { BusinessType, Confidence, Core, CoreField, ModuleKey, ModuleState, Route, SetupDraft } from "./setupRules";

/** What was saved on the server: the settings versions, and the modules whose suggested state was recorded. */
export interface SetupSaved {
  core: number | null;
  route: number | null;
  modules: ModuleKey[];
}

/** read_website and read_text. `notes` are plain sentences to show the owner (for example "Drafted with simple rules
 *  ..."). `drafted` is false when nothing at all could be drafted; older servers leave it and `drafted_by` out. */
export interface ReadAnswer {
  draft: SetupDraft;
  saved: SetupSaved;
  notes: string[];
  drafted_by?: "claude" | "rules";
  drafted?: boolean;
  skipped?: { url: string; why: string }[];
}

/** What a reading drafted, for the screen: false when neither what you sell nor who you serve came back. */
export function readDrafted(a: ReadAnswer): boolean {
  if (typeof a.drafted === "boolean") return a.drafted;
  const c = a.draft?.core;
  return !!c && (c.what_we_sell.trim() !== "" || c.who_we_serve.trim() !== "");
}

/** suggest_route. */
export interface RouteAnswer {
  route: Route;
  business_type: BusinessType;
  modules: SetupDraft["modules"];
  parts: SetupDraft["parts"];
  drafted_by: "claude" | "rules";
  saved: SetupSaved;
  notes: string[];
}

export type SetupRequest =
  | { action: "read_website"; url: string }
  | { action: "read_text"; text: string }
  | { action: "suggest_route"; goal_text: string };

export class SetupError extends Error {
  readonly status: number;
  /** "read_text" when the website couldn't be read: offer the few-lines box instead. */
  readonly fallback: "read_text" | null;
  constructor(message: string, status: number, fallback: "read_text" | null = null) {
    super(message);
    this.status = status;
    this.fallback = fallback;
  }
}

export interface SetupCallOptions {
  token: string;
  url?: string;
  anonKey?: string;
  fetchImpl?: typeof fetch;
}

/** One action on the setup Edge Function. */
export async function callSetup<T>(body: SetupRequest, opts: SetupCallOptions, isAnswer: (b: Record<string, unknown>) => boolean): Promise<T> {
  const f = opts.fetchImpl ?? httpFetch;
  let res: Response;
  try {
    res = await f(opts.url ?? functionUrl("setup"), {
      method: "POST",
      headers: { "content-type": "application/json", authorization: `Bearer ${opts.token}`, apikey: opts.anonKey ?? env.supabaseAnonKey },
      body: JSON.stringify(body),
    });
  } catch {
    throw new SetupError("Can't reach the server. Check your connection and try again.", 0);
  }
  const b = (await res.json().catch(() => ({}))) as Record<string, unknown> & { error?: string; fallback?: string };
  if (res.status === 404 && !b.error) {
    throw new SetupError("Reading your website isn't switched on yet. Write a few lines about your business instead.", 404, "read_text");
  }
  if (res.status === 401) throw new SetupError("Your session has ended. Sign in again.", 401);
  if (!res.ok || !isAnswer(b)) {
    throw new SetupError(typeof b.error === "string" && b.error ? b.error : "Something went wrong. Try again in a moment.", res.status,
      b.fallback === "read_text" ? "read_text" : null);
  }
  return b as T;
}

const isRead = (b: Record<string, unknown>) => !!b.draft && typeof b.draft === "object" && !!b.saved;
const isRoute = (b: Record<string, unknown>) => !!b.route && typeof b.route === "object" && !!b.modules && !!b.saved;

/** 0.35 Read the business's own website (up to six pages) and draft the set-up. */
export function readWebsite(url: string, opts: SetupCallOptions): Promise<ReadAnswer> {
  return callSetup<ReadAnswer>({ action: "read_website", url: url.trim() }, opts, isRead);
}

/** 0.35 without a website: a few lines the owner typed. */
export function readText(text: string, opts: SetupCallOptions): Promise<ReadAnswer> {
  return callSetup<ReadAnswer>({ action: "read_text", text: text.trim() }, opts, isRead);
}

/** 0.37 The owner's goal in a sentence becomes a suggested first module, its reason and module states. The route is not
 *  saved: the screen shows the suggestion and saves it with `crm.setRoute` when the owner says "Use this". */
export function suggestRoute(goal: string, opts: SetupCallOptions): Promise<RouteAnswer> {
  return callSetup<RouteAnswer>({ action: "suggest_route", goal_text: goal.trim() }, opts, isRoute);
}

// ---------------------------------------------------------------------------- demo stand-in
const DEMO_NOTE = "Demo: drafted with simple rules, not Claude. Please check each line.";

/** The setup function's answer in demo mode, from the keyword rules. Nothing is saved (`saved` is empty); the demo's
 *  own state handles confirmation. Returns [status, body] like the real function, including its refusals. */
export function demoSetupAnswer(body: unknown): [number, ReadAnswer | RouteAnswer | { error: string; fallback?: string }] {
  const b = (body && typeof body === "object" ? body : {}) as Record<string, unknown>;
  const saved: SetupSaved = { core: null, route: null, modules: [] };
  if (b.action === "read_website") {
    const url = typeof b.url === "string" ? b.url.trim() : "";
    if (!url) return [400, { error: "Type your website address, for example example.co.uk." }];
    const full = /^https?:\/\//i.test(url) ? url : `https://${url}`;
    let host = "";
    try {
      host = new URL(full).hostname.replace(/^www\./, "");
    } catch {
      return [400, { error: "That doesn't look like a website address." }];
    }
    // No network in demo mode: the address's own words stand in for the page text.
    const words = host.split(".")[0]!.replace(/[-_]+/g, " ");
    const d = draftFromText(`${words}.`, new URL(full).toString());
    return [200, { draft: d, saved, notes: [DEMO_NOTE], drafted_by: "rules", drafted: true }];
  }
  if (b.action === "read_text") {
    const text = typeof b.text === "string" ? b.text.trim() : "";
    if (text.length < 20) return [400, { error: "Write a few lines about your business: what you sell and who buys it." }];
    const d = draftFromText(text, null);
    return [200, { draft: d, saved, notes: [DEMO_NOTE], drafted_by: "rules", drafted: !!(d.core.what_we_sell || d.core.who_we_serve) }];
  }
  if (b.action === "suggest_route") {
    const goal = typeof b.goal_text === "string" ? b.goal_text.trim() : "";
    if (goal.length < 3) return [400, { error: "Say what you'd like Ogma to help with, in a sentence." }];
    const r = routeFromGoal(goal);
    const type = r.business_type ?? "unclear";
    return [200, {
      route: { goal: r.goal, first_module: r.first_module, source: "owner", reason: r.reason },
      business_type: type,
      modules: defaultModules(r.first_module, r.reason),
      parts: defaultParts(type),
      drafted_by: "rules",
      saved,
      notes: [DEMO_NOTE],
    }];
  }
  return [400, { error: "That action isn't available." }];
}
